import { Alert, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { useTreeViewApiRef } from "@mui/x-tree-view/hooks/useTreeViewApiRef";
import {
  DetailsTree,
  DetailsTreeItem,
  DetailsTreeItemValue,
  formatDate,
  formatDollarAmount,
} from "@synota-io/synota-shared-ui";
import { useRef, useState } from "react";
import type { ReactElement } from "react";
import {
  IServiceChargeRate,
  useServiceChargeRates,
} from "../../../../api/hooks/useServiceChargeRates";
import { IDashboardContract } from "../../../../api/hooks/useContracts";
import { IDashboardContractDetails } from "../../../../api/hooks/useContractDetails";

function getRateDates(rate: IServiceChargeRate) {
  if (rate.start_date && !rate.end_date) {
    return `Effective ${formatDate(rate.start_date)}`;
  }

  if (!rate.start_date && rate.end_date) {
    return `Effective immediately until ${formatDate(rate.end_date)}`;
  }

  if (rate.start_date && rate.end_date) {
    return `Effective ${formatDate(rate.start_date)} to ${formatDate(rate.end_date)}`;
  }

  return null;
}

function contractRateToTreeItem({ rate }: { rate: IServiceChargeRate }): DetailsTreeItemValue {
  const dates = getRateDates(rate);
  return {
    id: rate.shared_uuid,
    label: rate.description,
    action: rate.approved_by_payer ? (
      <Alert sx={{ bgcolor: "transparent" }}>Approved</Alert>
    ) : (
      <Alert severity="info" color="warning" sx={{ bgcolor: "transparent" }}>
        Pending Approval
      </Alert>
    ),
    value: `${formatDollarAmount(Number(rate.rate) * 1000, 3)}/MWh`,
    footer: rate.service_charge_provider_name && (
      <Stack>
        <Typography variant="caption" color="text.disabled">
          {rate.service_charge_provider_name}
        </Typography>
        {dates ? (
          <Typography fontStyle="italic" variant="caption" color="text.disabled">
            {dates}
          </Typography>
        ) : null}
      </Stack>
    ),
  };
}

export const ContractServiceChargeRates = ({
  contract,
  label = "Service Charge Rates",
  action = null,
}: {
  contract?: IDashboardContractDetails | IDashboardContract | null;
  label?: string;
  action?: ReactElement | null;
}) => {
  const apiRef = useTreeViewApiRef();
  const paperRef = useRef<HTMLDivElement>(null);

  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  const { rates, isFetching } = useServiceChargeRates({
    contract_shared_uuid: contract?.shared_uuid,
  });

  if (!contract || !rates.length) {
    return null;
  }

  const items: DetailsTreeItemValue[] = [
    {
      id: "root",
      label,
      action,
      children: rates.map((rate) =>
        contractRateToTreeItem({
          rate,
        }),
      ),
    },
  ];

  if (isFetching) {
    return <Skeleton variant="rounded" height={54} />;
  }

  return (
    <Paper tabIndex={-1} ref={paperRef} elevation={0}>
      <DetailsTree
        apiRef={apiRef}
        items={items}
        slots={{ item: DetailsTreeItem }}
        selectedItems={selectedItem}
        onSelectedItemsChange={(_event, itemId) => {
          if (itemId === items[0].id) {
            paperRef.current?.focus();
            return;
          }

          setSelectedItem(itemId);
        }}
      />
    </Paper>
  );
};
